import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { actionCorreosGet } from "../../redux/actions/correos/correos";
import { relativeTime } from "../../utils/relativeTime";
import "../../assets/css/correos.css";
import "../../assets/css/notificaciones.css";

const ensureHeadLink = (id, href) => {
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id; link.rel = "stylesheet"; link.href = href;
  document.head.appendChild(link);
};

const ALL_CATEGORIES = [
  "Audiencias Conciliacion",
  "Convenios de Pago",
  "Audiencias Preliminares",
];

const CATEGORY_COLOR = {
  "Audiencias Conciliacion": { bg: "#eff6ff", color: "#1d4ed8" },
  "Convenios de Pago":       { bg: "#f0fdf4", color: "#16a34a" },
  "Audiencias Preliminares": { bg: "#fdf4ff", color: "#7c3aed" },
};

function cardTitle(item) {
  const trabajador = item.nombre_trabajador;
  const empresa    = item.nombre_empresa;
  if (trabajador && empresa) return `${trabajador} vs ${empresa}`;
  if (trabajador) return trabajador;
  return item.evento_titulo || item.evento_tipo || `Correo #${item.id_correo}`;
}

function CorreosPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    "Audiencias Conciliacion": true,
    "Convenios de Pago":       true,
    "Audiencias Preliminares": true,
  });

  useEffect(() => {
    ensureHeadLink(
      "gfont-public-sans",
      "https://fonts.googleapis.com/css2?family=Public+Sans:wght@300;400;500;600;700;800;900&display=swap"
    );
    ensureHeadLink(
      "gfont-material-symbols",
      "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap"
    );
  }, []);

  const fetchCorreos = useCallback(
    (activeFilters) => {
      const selected = ALL_CATEGORIES.filter((c) => activeFilters[c]);
      setLoading(true);
      dispatch(actionCorreosGet(selected))
        .then((data) => setItems(data?.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchCorreos(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFilter = (key) => {
    const next = { ...filters, [key]: !filters[key] };
    setFilters(next);
    fetchCorreos(next);
  };

  const counts = ALL_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = items.filter((i) => {
      const tipo = (i.evento_tipo || "").toLowerCase();
      if (cat === "Convenios de Pago")       return tipo.includes("onvenio");
      if (cat === "Audiencias Preliminares") return tipo.includes("reliminar");
      return !tipo.includes("onvenio") && !tipo.includes("reliminar");
    }).length;
    return acc;
  }, {});

  return (
    <div className="ll-root">
      <main className="ll-main">
        <aside className="ll-aside">
          <div>
            <h1 className="ll-aside-title">Categorías</h1>

            {ALL_CATEGORIES.map((cat) => {
              const active  = filters[cat];
              const colors  = CATEGORY_COLOR[cat];
              return (
                <label key={cat} className="ll-check" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleFilter(cat)}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500, color: active ? "#0f172a" : "#94a3b8" }}>
                    {cat}
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      background: active ? colors.bg : "#f1f5f9",
                      color: active ? colors.color : "#94a3b8",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "2px 8px",
                    }}
                  >
                    {counts[cat] ?? 0}
                  </span>
                </label>
              );
            })}
          </div>
        </aside>

        <section className="ll-content">
          <div className="ll-content-head">
            <div className="ll-titlewrap">
              <h2 className="ll-title">Centro de Correos</h2>
            </div>
          </div>

          <div className="ll-list">
            {loading ? (
              <div className="cr-empty">
                <div className="cr-empty-icon">⏳</div>
                <p>Cargando historial…</p>
              </div>
            ) : items.length === 0 ? (
              <div className="cr-empty">
                <div className="cr-empty-icon">📭</div>
                <p>No hay correos para mostrar.</p>
              </div>
            ) : (
              items.map((item) => {
                const isManana  = item.is_manana === 1;
                const tipo      = (item.evento_tipo || "").toLowerCase();
                const categoria = tipo.includes("onvenio")
                  ? "Convenios de Pago"
                  : tipo.includes("reliminar")
                    ? "Audiencias Preliminares"
                    : "Audiencias Conciliacion";
                const colors    = CATEGORY_COLOR[categoria];
                const titulo    = cardTitle(item);
                const fechaEnvio = relativeTime(item.datetime);
                const fechaEvento = item.evento_fecha
                  ? new Date(item.evento_fecha).toLocaleString("es-MX", {
                      day: "numeric", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })
                  : null;

                return (
                  <div
                    key={item.id_correo}
                    className="ll-card ll-border-slate"
                    role="article"
                  >
                    <div className="ll-card-row">
                      <div className="ll-card-body">
                        <div className="ll-card-top">
                          <div className="ll-meta">
                            <span
                              className="ll-badge ll-badge--newcase"
                              style={{ background: colors.bg, color: colors.color }}
                            >
                              {categoria}
                            </span>
                           
                            <span className="ll-time">Enviado {fechaEnvio}</span>
                          </div>
                        </div>

                        <h3 className="ll-card-title">{titulo}</h3>

                        <p className="ll-desc">
                          {fechaEvento ? <>Evento: {fechaEvento} &nbsp;·&nbsp;</> : null}
                          {item.total_enviados ?? 0} destinatario{item.total_enviados !== 1 ? "s" : ""}
                        </p>
                      </div>

                      <div className="ll-card-cta">
                        <button
                          type="button"
                          className="ll-ctabtn ll-ctabtn--neutral"
                          onClick={() => navigate(`/correos/${item.id_correo}`)}
                        >
                          <span>Ver Detalles</span>
                          <span className="material-symbols-outlined ll-icon ll-icon--arrow">arrow_forward</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default CorreosPage;
